import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as charts from 'echarts';
import { getFormattedDateArray } from '../../../shared/lib/dateUtils';
import { useTheme } from '../../../contexts/ThemeContext';
import { useMobile } from '../../../contexts/MobileContext';
import {
  getAxisChartGrid,
  colorToRgba,
  getFadedLineStroke,
  getChartGraphicLabel,
  getChartTheme,
  getChartTooltipStyle,
  getChartTypography,
} from '../charting/chartTheme';
import useChartAutoResize from '../charting/useChartAutoResize';
import Modal, { ModalCloseButton } from '../../../shared/modals/Modal';

import { ReactComponent as ExpandIcon } from '../../../assets/icons/expand.svg';

const NEUTRAL_SERIES_NAMES = new Set([
  '',
  '-',
  '--',
  'NA',
  'N/A',
  'NONE',
  'NULL',
  'UNKNOWN',
  'NOT SET',
  '(NOT SET)',
  'UNDEFINED',
  'OTHER',
]);

const getSeriesPalette = (theme) => ([...(theme.categoricalPalette || [])]);

const getSeriesColor = (theme, itemName, index) => {
  const normalizedName = String(itemName ?? '').trim().toUpperCase();

  if (NEUTRAL_SERIES_NAMES.has(normalizedName)) {
    const neutralPalette = theme.categoricalNeutralPalette || [];
    return neutralPalette[index % neutralPalette.length] || theme.textLow;
  }

  const palette = getSeriesPalette(theme);
  return palette[index % palette.length];
};

const getLegendItems = (values) => values.map((item) => ({
  name: item.name,
  color: item.color,
}));

const getLegendChipStyle = (color) => ({
  '--legend-chip-color': color,
  '--legend-chip-border': colorToRgba(color, 0.24),
  '--legend-chip-bg': colorToRgba(color, 0.10),
  '--legend-chip-active-border': colorToRgba(color, 0.44),
  '--legend-chip-active-bg': colorToRgba(color, 0.18),
  '--legend-chip-shadow': colorToRgba(color, 0.32),
});

const getOption = (values, lastDay, theme, isMobile) => {
  const typography = getChartTypography(isMobile);
  const palette = getSeriesPalette(theme);
  const normalizedValues = values.length === 0
    ? [{ name: 'No Data', data: [0], color: theme.strokeMid }]
    : values;
  const length = normalizedValues[0]?.data?.length || 0;
  const chartColors = normalizedValues.map((item, index) => item.color || palette[index % palette.length]);

  return {
    tooltip: getChartTooltipStyle(theme, typography, {
      confine: true,
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: (params) => {
        if (!params.length) return '';

        const date = new Date(params[0].name + ' 2024');
        const month = date.toLocaleString('default', { month: 'long' });
        const day = date.getDate();
        const sorted = [...params].sort((a, b) => b.value - a.value);
        const visibleItems = sorted.slice(0, 5);
        const remainingTotal = sorted
          .slice(5)
          .reduce((sum, item) => sum + item.value, 0);

        const lines = visibleItems.map((item) => (
          `<span style="display:inline-block;width:10px;height:10px;background-color:${item.color};margin-right:5px;"></span>${item.seriesName}: ${item.value}`
        ));

        if (sorted.length > visibleItems.length) {
          lines.push(`Other: ${remainingTotal}`);
        }

        return `${month} ${day}<br/>${lines.join('<br/>')}`;
      },
    }),
    xAxis: {
      type: 'category',
      data: getFormattedDateArray(lastDay, length),
      axisLine: {
        lineStyle: {
          color: theme.strokeMid,
          width: 1,
        },
      },
      axisLabel: {
        color: theme.textHigh,
        boundaryGap: false,
        interval: Math.ceil(length / 4),
        fontSize: typography.axisLabelSize,
        rotate: isMobile ? 30 : 0,
        hideOverlap: true,
      },
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitNumber: isMobile ? 3 : 4,
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: theme.textHigh,
        formatter: '{value}',
        fontSize: typography.axisLabelSize,
        hideOverlap: true,
      },
      splitLine: {
        lineStyle: {
          color: theme.strokeLow,
          type: 'dashed',
          width: 1,
        },
      },
    },
    grid: getAxisChartGrid(isMobile, { hasLegend: false }),
    color: chartColors,
    animationDurationUpdate: 140,
    animationEasingUpdate: 'cubicOut',
    series: normalizedValues.map((item, index) => {
      const seriesColor = item.color || chartColors[index % chartColors.length];
      return {
        name: item.name,
        symbol: 'circle',
        symbolSize: isMobile ? 8 : 10,
        type: 'line',
        data: item.data,
        smooth: 0.15,
        showSymbol: false,
        lineStyle: {
          width: isMobile ? 1.25 : 1.75,
          color: getFadedLineStroke(charts, seriesColor),
        },
      };
    }),
    graphic: [getChartGraphicLabel(theme, isMobile, 'Bot Clicks')],
    backgroundColor: 'transparent',
  };
};

const LegendChip = ({ item, selected, onToggle }) => (
  <button
    type="button"
    className={`analytics-series-legend-chip ${selected ? 'is-active' : 'is-inactive'}`}
    style={getLegendChipStyle(item.color)}
    title={item.name}
    aria-pressed={selected}
    onClick={() => onToggle(item.name)}
  >
    <span className="analytics-series-legend-chip__swatch" />
    <span className="analytics-series-legend-chip__label">{item.name}</span>
  </button>
);

const CompactLegend = ({
  legendItems,
  legendSelection,
  selectedLegendCount,
  onToggle,
  scrollRef,
  canScrollLeft,
  canScrollRight,
  onScroll,
  onScrollBy,
}) => {
  if (legendItems.length === 0) return null;

  return (
    <div className="analytics-series-legend-panel is-compact">
      <div className="analytics-series-legend-inline-meta">
        <span className="analytics-series-legend-count">
          {selectedLegendCount} / {legendItems.length} visible
        </span>
      </div>
      <div className="analytics-series-legend-track is-compact">
        <button
          type="button"
          className="btn btn-subtle analytics-series-legend-arrow"
          onClick={() => onScrollBy(-1)}
          disabled={!canScrollLeft}
          aria-label="Scroll legend left"
        >
          <span aria-hidden="true">◀</span>
        </button>
        <div
          ref={scrollRef}
          className="analytics-series-legend-scroll is-compact"
          onScroll={onScroll}
        >
          {legendItems.map((item, index) => (
            <LegendChip
              key={`${item.name}-${index}`}
              item={item}
              selected={legendSelection[item.name] !== false}
              onToggle={onToggle}
            />
          ))}
        </div>
        <button
          type="button"
          className="btn btn-subtle analytics-series-legend-arrow"
          onClick={() => onScrollBy(1)}
          disabled={!canScrollRight}
          aria-label="Scroll legend right"
        >
          <span aria-hidden="true">▶</span>
        </button>
      </div>
    </div>
  );
};

const ExpandedLegend = ({
  legendItems,
  legendSelection,
  selectedLegendCount,
  onToggle,
  onSetAll,
}) => {
  if (legendItems.length === 0) return null;

  return (
    <div className="analytics-series-legend-panel is-expanded">
      <div className="analytics-series-legend-controls">
        <div className="analytics-series-legend-actions">
          <button
            type="button"
            className="btn btn-subtle analytics-series-legend-control-btn"
            onClick={() => onSetAll(true)}
          >
            Select all
          </button>
          <button
            type="button"
            className="btn btn-subtle analytics-series-legend-control-btn"
            onClick={() => onSetAll(false)}
          >
            Clear all
          </button>
        </div>
        <div className="analytics-series-legend-meta">
          <span className="analytics-series-legend-count">
            {selectedLegendCount} / {legendItems.length}
          </span>
        </div>
      </div>
      <div className="analytics-series-legend-scroll is-expanded">
        {legendItems.map((item, index) => (
          <LegendChip
            key={`${item.name}-${index}`}
            item={item}
            selected={legendSelection[item.name] !== false}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  );
};

const Graph = ({ specs, setReady }) => {
  const { isMobile } = useMobile();
  const { isDarkMode } = useTheme();
  const theme = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const [expanded, setExpanded] = useState(false);
  const chartRef = useRef(null);
  const legendScrollRef = useRef(null);
  const [canScrollLegendLeft, setCanScrollLegendLeft] = useState(false);
  const [canScrollLegendRight, setCanScrollLegendRight] = useState(false);

  const initialValues = useMemo(
    () => specs.values.slice(0, 10).map((item, index) => ({
      ...item,
      color: getSeriesColor(theme, item.name, index),
      name: item.name?.toUpperCase() === 'NA' ? 'Direct' : item.name,
    })),
    [specs.values, theme]
  );
  const legendItems = useMemo(() => getLegendItems(initialValues), [initialValues]);
  const [legendSelection, setLegendSelection] = useState(() =>
    Object.fromEntries(initialValues.map((item) => [item.name, true]))
  );

  useEffect(() => {
    setLegendSelection((previous) => {
      const next = {};
      let changed = false;

      initialValues.forEach((item) => {
        const value = previous[item.name] ?? true;
        next[item.name] = value;
        if (previous[item.name] !== value) changed = true;
      });

      if (Object.keys(previous).length !== initialValues.length) changed = true;
      return changed ? next : previous;
    });
  }, [initialValues]);

  const visibleValues = useMemo(
    () => initialValues.filter((item) => legendSelection[item.name] !== false),
    [initialValues, legendSelection]
  );
  const selectedLegendCount = legendItems.reduce(
    (count, item) => count + (legendSelection[item.name] !== false ? 1 : 0),
    0
  );
  const chartOption = useMemo(
    () => getOption(visibleValues, specs.lastDay, theme, isMobile),
    [visibleValues, specs.lastDay, theme, isMobile]
  );
  const canExpand = legendItems.length > 0;

  useChartAutoResize(chartRef, [isDarkMode, isMobile, specs, expanded, visibleValues.length]);

  const updateLegendScrollButtons = useCallback(() => {
    const node = legendScrollRef.current;
    if (!node || expanded) {
      setCanScrollLegendLeft(false);
      setCanScrollLegendRight(false);
      return;
    }

    const maxLeft = Math.max(0, node.scrollWidth - node.clientWidth);
    setCanScrollLegendLeft(node.scrollLeft > 1);
    setCanScrollLegendRight(node.scrollLeft < maxLeft - 1);
  }, [expanded]);

  const scrollLegendBy = useCallback((direction) => {
    const node = legendScrollRef.current;
    if (!node) return;

    const distance = Math.max(96, Math.round(node.clientWidth * 0.65));
    node.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const node = legendScrollRef.current;
    if (!node || expanded) {
      updateLegendScrollButtons();
      return;
    }

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateLegendScrollButtons())
      : null;

    if (observer) {
      observer.observe(node);
      if (node.parentElement) {
        observer.observe(node.parentElement);
      }
    }

    const raf = requestAnimationFrame(updateLegendScrollButtons);

    return () => {
      cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
    };
  }, [expanded, isMobile, legendItems, updateLegendScrollButtons]);

  const handleChartReady = (chart) => {
    chart.off('finished');
    chart.on('finished', () => {
      setReady(specs.title.toLowerCase());
    });
  };

  const toggleLegendSelection = (name) => {
    setLegendSelection((previous) => ({
      ...previous,
      [name]: !(previous[name] !== false),
    }));
  };

  const setAllLegendSelection = (isSelected) => {
    setLegendSelection(
      Object.fromEntries(legendItems.map((item) => [item.name, isSelected]))
    );
  };

  const renderChart = (viewKey) => (
    <ReactECharts
      option={chartOption}
      opts={{ renderer: isMobile ? 'svg' : 'canvas' }}
      onChartReady={handleChartReady}
      className="chart site-overview-chart-canvas"
      key={`${isDarkMode ? 'dark' : 'light'}-${isMobile ? 'm' : 'd'}-${viewKey}`}
      ref={chartRef}
      notMerge={true}
    />
  );

  return (
    <div className="analytics-series-frame">
      {!expanded && (
        <div className="analytics-series-graph">
          <div className="simple-row padding-b space-between analytics-series-graph__header">
            <div className="chart-title">{specs.title}</div>
            {canExpand && (
              <button
                type="button"
                className="control-btn traffic-graph__expand-btn"
                aria-label={`Expand ${specs.title}`}
                onClick={() => setExpanded(true)}
              >
                <ExpandIcon className="icon sm image" />
              </button>
            )}
          </div>
          <div className="analytics-series-graph__body">
            <div className="chart-wrap analytics-series-graph__chart-wrap">
              {renderChart('inline')}
            </div>
            <CompactLegend
              legendItems={legendItems}
              legendSelection={legendSelection}
              selectedLegendCount={selectedLegendCount}
              onToggle={toggleLegendSelection}
              scrollRef={legendScrollRef}
              canScrollLeft={canScrollLegendLeft}
              canScrollRight={canScrollLegendRight}
              onScroll={updateLegendScrollButtons}
              onScrollBy={scrollLegendBy}
            />
          </div>
        </div>
      )}

      {expanded && (
        <Modal
          isOpen={expanded}
          onClose={() => setExpanded(false)}
          className="modal--wide modal--full-height"
        >
          <div className="modal">
            <div className="modal__header">
              <h3 className="margin-none">{specs.title}</h3>
              <ModalCloseButton onClick={() => setExpanded(false)} label="Close chart modal" />
            </div>
            <div className="modal__body">
              <div className="site-overview">
                <div className="analytics-series-graph analytics-series-graph--modal">
                  <ExpandedLegend
                    legendItems={legendItems}
                    legendSelection={legendSelection}
                    selectedLegendCount={selectedLegendCount}
                    onToggle={toggleLegendSelection}
                    onSetAll={setAllLegendSelection}
                  />
                  <div className="chart-wrap analytics-series-graph__chart-wrap analytics-series-graph__chart-wrap--modal">
                    {renderChart('modal')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Graph;
